import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSettings, useUpdateSettings } from '@/features/app/hooks';
import type { RegraAtraso } from '@/types';
import { AppLayout } from '@/components/AppLayout';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/components/ui/Toast';

const REGRAS: RegraAtraso[] = ['SemanaAtual', 'Ultimos7Dias', 'MesAtual'];

/** Administrador-only tunables: expected hours, the backdating window, reminders. */
export function DefinicoesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const settings = useSettings();
  const guardar = useUpdateSettings();

  const [horas, setHoras] = useState('8');
  const [regra, setRegra] = useState<RegraAtraso>('SemanaAtual');
  const [mostrarCC, setMostrarCC] = useState(true);
  const [avisoAtivo, setAvisoAtivo] = useState(true);
  const [avisoHora, setAvisoHora] = useState('18:00');

  useEffect(() => {
    if (!settings.data) return;
    setHoras(String(settings.data.horasAlvoDiarias));
    setRegra(settings.data.regraAtraso);
    setMostrarCC(settings.data.mostrarCentroCusto);
    setAvisoAtivo(settings.data.avisoDiarioAtivo);
    setAvisoHora(settings.data.avisoDiarioHora);
  }, [settings.data]);

  const submit = async () => {
    try {
      await guardar.mutateAsync({
        horasAlvoDiarias: Number(horas.replace(',', '.')) || 8,
        regraAtraso: regra,
        mostrarCentroCusto: mostrarCC,
        avisoDiarioAtivo: avisoAtivo,
        avisoDiarioHora: avisoHora,
      });
      toast.show(t('definicoes.guardado'));
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  return (
    <AppLayout title={t('definicoes.titulo')} subtitle={t('perfil.definicoesSub')}>
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => navigate('/perfil')}
          className="flex items-center gap-1.5 self-start rounded-full border border-conduril-border bg-white py-2 pl-2.5 pr-3.5 text-[11px] font-semibold text-conduril"
        >
          <Icon name="arrow_back" className="text-[16px]" /> {t('header.perfil')}
        </button>

        {settings.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {settings.error && <ErrorBanner error={settings.error} onRetry={() => void settings.refetch()} />}

        {settings.data && (
          <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-start md:gap-4">
            <section className="card p-4">
              <div className="text-[13px] font-semibold text-conduril-ink">
                {t('definicoes.horasAlvo')}
              </div>
              <p className="mt-1 text-[11px] leading-snug text-conduril-muted">
                {t('definicoes.horasAlvoSub')}
              </p>
              <input
                className="field num mt-3 bg-[#fbfbfd] text-[15px]"
                inputMode="decimal"
                value={horas}
                onChange={(e) => setHoras(e.target.value.replace(/[^\d.,]/g, ''))}
              />
            </section>

            <section className="card p-4">
              <div className="text-[13px] font-semibold text-conduril-ink">{t('definicoes.regra')}</div>
              <p className="mt-1 text-[11px] leading-snug text-conduril-muted">
                {t('definicoes.regraSub')}
              </p>
              <div className="mt-3 flex flex-col gap-1.5">
                {REGRAS.map((opcao) => {
                  const ativo = regra === opcao;
                  return (
                    <button
                      key={opcao}
                      type="button"
                      onClick={() => setRegra(opcao)}
                      className={`flex items-center justify-between gap-2.5 rounded-xl border px-3.5 py-3 text-left transition ${
                        ativo ? 'border-conduril bg-[#f2f4fb]' : 'border-conduril-border bg-white'
                      }`}
                    >
                      <span className="text-[13px] font-medium text-conduril-ink">
                        {t(`definicoes.regra${opcao}`)}
                      </span>
                      <span
                        className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                          ativo ? 'border-conduril bg-conduril' : 'border-[#ccd1dd] bg-white'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="card flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-conduril-ink">
                  {t('definicoes.mostrarCC')}
                </div>
                <p className="mt-1 text-[11px] leading-snug text-conduril-muted">
                  {t('definicoes.mostrarCCSub')}
                </p>
              </div>
              <Toggle checked={mostrarCC} onChange={setMostrarCC} label={t('definicoes.mostrarCC')} />
            </section>

            <section className="card p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-conduril-ink">
                    {t('definicoes.aviso')}
                  </div>
                  <p className="mt-1 text-[11px] leading-snug text-conduril-muted">
                    {t('definicoes.avisoSub')}
                  </p>
                </div>
                <Toggle checked={avisoAtivo} onChange={setAvisoAtivo} label={t('definicoes.aviso')} />
              </div>
              {avisoAtivo && (
                <div className="mt-3">
                  <div className="label-eyebrow">{t('definicoes.avisoHora')}</div>
                  <input
                    type="time"
                    className="field num mt-2 bg-[#fbfbfd] text-[15px]"
                    value={avisoHora}
                    onChange={(e) => setAvisoHora(e.target.value)}
                  />
                </div>
              )}
            </section>

            <button
              type="button"
              disabled={guardar.isPending}
              onClick={() => void submit()}
              className="btn-primary md:col-span-2 md:w-auto md:justify-self-start md:px-8"
            >
              {t('common.save')}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
